import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const ParentEmergencyAlert = ({ onNavigate, userInfo }) => {
  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentData, setParentData] = useState({
    name: "Loading...",
    parentId: "...",
    relation: "Parent",
    profileImage: null
  });

  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    rollNumber: "...",
    course: "...",
    department: "...",
    year: "...",
    batch: "..."
  });

  // Emergency Alert State
  const [emergencyAlert, setEmergencyAlert] = useState({
    type: 'medical',
    message: '',
    location: '',
    priority: 'high',
    isSubmitting: false
  });

  // Emergency Types
  const emergencyTypes = [
    { value: 'medical', label: 'Medical Emergency', icon: '🏥', color: '#dc2626' },
    { value: 'accident', label: 'Accident', icon: '🚑', color: '#ea580c' },
    { value: 'missing', label: 'Missing Student', icon: '🔍', color: '#7c3aed' },
    { value: 'security', label: 'Security Issue', icon: '🔒', color: '#059669' },
    { value: 'natural', label: 'Natural Disaster', icon: '⛈️', color: '#0369a1' },
    { value: 'other', label: 'Other Emergency', icon: '⚠️', color: '#9333ea' }
  ];

  // Fetch parent data from API
  useEffect(() => {
    fetchParentData();
  }, [userInfo]);

  const fetchParentData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Starting API call to fetch parent data...');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');

      if (!token) {
        console.log('❌ No token found, using fallback data');
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      const profileUrl = `${API_LINK}/api/parent/profile`;
      console.log('🌐 Profile API URL:', profileUrl);

      const profileResponse = await fetch(profileUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Profile Response status:', profileResponse.status);

      if (!profileResponse.ok) {
        if (profileResponse.status === 401) {
          console.log('🔐 Authentication failed');
          setError('Session expired. Please login again.');
          handleLogout();
          return;
        } else if (profileResponse.status === 404) {
          console.log('🔍 API endpoint not found');
          setError('Profile data endpoint not found. Please check server configuration.');
          setLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${profileResponse.status}`);
      }

      const profileResult = await profileResponse.json();
      console.log('📦 Profile API Response:', profileResult);

      if (profileResult.success && profileResult.data) {
        const data = profileResult.data;
        console.log('✅ Processing profile API data...');

        // Update parent data
        setParentData({
          name: data.name || "Parent",
          parentId: data.parentId || "PARENT-001",
          relation: data.relation || "Parent",
          profileImage: data.profileImage || null
        });

        // Update student data
        setStudentData({
          name: data.student?.name || "Student",
          studentId: data.student?.studentId || "STU-001",
          rollNumber: data.student?.rollNumber || "...",
          course: data.student?.course || "...",
          department: data.student?.department || "...",
          year: data.student?.year || "...",
          batch: data.student?.batch || "..."
        });

        console.log('✅ Parent data updated successfully');

      } else {
        console.log('❌ Profile API returned error:', profileResult.message);
        setError(profileResult.message || 'Failed to fetch profile data');
      }

    } catch (error) {
      console.error('💥 Error fetching parent data:', error);

      if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
        setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
      } else if (error.message.includes('fetch')) {
        setError('Cannot connect to server. Please ensure the server is running');
      } else {
        setError(`Failed to load profile data: ${error.message}`);
      }

      if (userInfo) {
        console.log('🔄 Using fallback userInfo data');
        setParentData(prev => ({
          ...prev,
          name: userInfo.name || prev.name,
          parentId: userInfo.id || prev.parentId
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');
    sessionStorage.clear();
    localStorage.clear();

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    switch (page) {
      case 'Dashboard':
        onNavigate('parent-dashboard');
        break;
      case 'Academic Performance':
        onNavigate('parent-academic-performance');
        break;
      case 'Fee Status':
        onNavigate('parent-fees-status');
        break;
      case 'Holidays & Events':
        onNavigate('parent-holidays');
        break;
      case 'Emergency Alert':
        // Stay on current page
        break;
      case 'Logout':
        handleLogout();
        break;
      default:
        console.log(`Navigation to ${page} clicked`);
    }
  };

  // Handle emergency submission
  const handleEmergencySubmit = async () => {
    if (!emergencyAlert.message.trim()) {
      alert('Please enter a message for the emergency alert');
      return;
    }

    setEmergencyAlert(prev => ({ ...prev, isSubmitting: true }));

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        alert('Authentication required. Please login again.');
        handleLogout();
        return;
      }

      const response = await fetch(`${API_LINK}/api/parent/emergency/alert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: emergencyAlert.type,
          message: emergencyAlert.message,
          location: emergencyAlert.location,
          priority: emergencyAlert.priority
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(`Emergency alert sent successfully! Alert ID: ${result.data.alertId}\n\nSchool administration has been notified immediately.`);
        setEmergencyAlert({
          type: 'medical',
          message: '',
          location: '',
          priority: 'high',
          isSubmitting: false
        });
      } else {
        throw new Error(result.message || 'Failed to send emergency alert');
      }
    } catch (error) {
      console.error('Error sending emergency alert:', error);
      alert(`Failed to send emergency alert: ${error.message}`);
    } finally {
      setEmergencyAlert(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Emergency Alert...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Emergency Alert
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="parent"
      onNavigate={handleNavigation}
      parentData={parentData}
      activePage="Emergency Alert"
      pageTitle="Emergency Alert"
    >
      {/* Emergency Alert Header */}
      <section style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        border: '2px solid #dc2626'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', color: '#dc2626' }}>🚨</div>

          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#dc2626',
            marginBottom: '1rem'
          }}>
            Emergency Alert System
          </h1>

          <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '1.5rem' }}>
            Send an immediate emergency alert to the school administration about your child - {studentData.name}
          </p>

          <div style={{
            backgroundColor: '#fef2f2',
            borderRadius: '12px',
            padding: '1rem',
            fontSize: '0.9rem',
            color: '#991b1b',
            border: '1px solid #fecaca'
          }}>
            ⚠️ <strong>IMPORTANT:</strong> Use this feature only for genuine emergencies. False alerts may result in penalties.
            Emergency services and school administration will be notified immediately.
          </div>
        </div>

        {/* Student Information Display */}
        <div style={{
          background: '#f8fafc',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Student Information</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
            <p style={{ margin: 0, color: '#666' }}><strong>Name:</strong> {studentData.name}</p>
            <p style={{ margin: 0, color: '#666' }}><strong>Student ID:</strong> {studentData.studentId}</p>
            <p style={{ margin: 0, color: '#666' }}><strong>Class:</strong> {studentData.course} - {studentData.year}</p>
            <p style={{ margin: 0, color: '#666' }}><strong>Roll Number:</strong> {studentData.rollNumber}</p>
          </div>
        </div>
      </section>

      {/* Emergency Alert Form */}
      <section style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h3 style={{ margin: '0 0 1.5rem 0', color: '#333', fontSize: '1.4rem' }}>
          Emergency Alert Form
        </h3>

        {/* Emergency Type Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.75rem',
            fontWeight: 'bold',
            color: '#333',
            fontSize: '1rem'
          }}>
            Emergency Type *
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem'
          }}>
            {emergencyTypes.map((type) => (
              <div
                key={type.value}
                onClick={() => setEmergencyAlert(prev => ({ ...prev, type: type.value }))}
                style={{
                  padding: '1rem',
                  border: `2px solid ${emergencyAlert.type === type.value ? type.color : '#e5e7eb'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: emergencyAlert.type === type.value ? `${type.color}10` : '#ffffff',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}
                onMouseEnter={(e) => {
                  if (emergencyAlert.type !== type.value) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (emergencyAlert.type !== type.value) {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>{type.icon}</span>
                <div>
                  <div style={{ fontWeight: '600', color: '#333' }}>{type.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority Level */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Priority Level *
          </label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {['low', 'medium', 'high', 'critical'].map(level => (
              <label key={level} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                border: `1px solid ${emergencyAlert.priority === level ? '#dc2626' : '#e5e7eb'}`,
                backgroundColor: emergencyAlert.priority === level ? '#fef2f2' : '#ffffff',
                color: emergencyAlert.priority === level ? '#dc2626' : '#666',
                fontWeight: emergencyAlert.priority === level ? 'bold' : 'normal'
              }}>
                <input
                  type="radio"
                  name="priority"
                  value={level}
                  checked={emergencyAlert.priority === level}
                  onChange={() => setEmergencyAlert(prev => ({ ...prev, priority: level }))}
                  style={{ accentColor: '#dc2626' }}
                />
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </label>
            ))}
          </div>
        </div>

        {/* Location */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Current Location/Last Known Location *
          </label>
          <input
            type="text"
            value={emergencyAlert.location}
            onChange={(e) => setEmergencyAlert(prev => ({ ...prev, location: e.target.value }))}
            placeholder="e.g., Near Library, Canteen, Main Gate, etc."
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '1rem'
            }}
          />
        </div>

        {/* Message */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Details of Emergency *
          </label>
          <textarea
            value={emergencyAlert.message}
            onChange={(e) => setEmergencyAlert(prev => ({ ...prev, message: e.target.value }))}
            placeholder="Please describe the situation in detail..."
            rows={5}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '1rem',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleEmergencySubmit}
          disabled={emergencyAlert.isSubmitting}
          style={{
            width: '100%',
            padding: '1rem',
            backgroundColor: '#dc2626',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            cursor: emergencyAlert.isSubmitting ? 'not-allowed' : 'pointer',
            opacity: emergencyAlert.isSubmitting ? 0.7 : 1,
            transition: 'all 0.3s ease',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            if (!emergencyAlert.isSubmitting) e.target.style.backgroundColor = '#b91c1c';
          }}
          onMouseLeave={(e) => {
            if (!emergencyAlert.isSubmitting) e.target.style.backgroundColor = '#dc2626';
          }}
        >
          {emergencyAlert.isSubmitting ? 'Sending Alert...' : '🚨 SEND EMERGENCY ALERT'}
        </button>
      </section>
    </Layout>
  );
};

export default ParentEmergencyAlert;